import { BlueprintContext } from '../blueprint-context/blueprint-context.class';
import { BlueprintInstanceType } from '../shared.enums';
import { Kitchen } from './kitchen.class';
import { NoteType } from '../../note-classes/shared.enums';

import { deleteItemByIdCfn } from '../shared.functions';
import { BlueprintBase, BlueprintOptions } from '../blueprint-base';
import { KitchenDataIn, KitchensDataIn, KitchensDataOut } from './types';

export class Kitchens extends BlueprintBase<KitchensDataIn> {
	public children: Array<Kitchen>;

	constructor (context: BlueprintContext, options: BlueprintOptions<KitchensDataIn> = {}) {
		super(BlueprintInstanceType.Kitchens, options, context);

		const { dataIn: data = null } = options;
		this.children = [];
		if (data && data.children && data.children.length > 0) {
			data.children.forEach((n) => this.children.push(new Kitchen({ dataIn: n }, this.context)));
		} else {
			// should always have at least one kitchen
			this.children.push(new Kitchen({}, this.context));
		}

		this.setInitialized();
	}

	get latestVersion (): number { return this.getLatestVersion()?.version ?? 0 }

	/**
	 * Serializes supplied kitchen then deserializes with the proper context
	 * @param kitchen kitchen instance to add
	 */
	public addKitchen (kitchen: Kitchen): void {
		this.newKitchen(kitchen.serialize());
	}

	public delete (id: string): boolean {
		const removed = deleteItemByIdCfn(this.children, id);
		// kitchen instances share child ids — purge cached references to the removed tree
		if (removed) this.context.cache.clear();
		return removed;
	}

	/**
	 * creates a new kitchen, blank or deserialized
	 * @param dataIn partial kitchen data
	 * @returns Kitchen instance
	 */
	public newKitchen (dataIn: KitchenDataIn = null): Kitchen {
		const kitchen = new Kitchen({ dataIn }, this.context);
		this.children.push(kitchen);
		this.context.cache.clear();
		return kitchen;
	}

	/**
	 * Saves a revision of the latest version
	 * @param revisionNote note to add to the latest version
	 */
	public saveRevision (revisionNote: string): void {
		const latest = this.getLatestVersion();
		if (latest) {
			latest.notes.add({ message: revisionNote, artifactId: latest.id, type: NoteType.Revision });
		}
		this.newVersionFromLatest();
	}

	/**
	 *
	 * @param version version of kitchen to locate
	 * @returns kitchen, null when kitchen version wasn't located
	 */
	public findVersion (version: number | null): Kitchen | null {
		const found = this.children.find((k) => k.version === version) ?? null;
		// console.log('draftKitchen:findVersion', version, this.map((k) => k.version));
		return found;
	}

	/**
	 *
	 * @returns kitchen representing the active version, which is null when an active edit version is available
	 */
	public getActiveVersion (): Kitchen {
		const kv = this.findVersion(null);
		return kv ? kv : this.getLatestVersion();
	}

	/**
	 *
	 * @returns kitchen representing the most recent version
	 */
	public getLatestVersion (): Kitchen {
		// return null if no version exist
		if (this.children.length === 0) return null;

		// sort a copy in descending order — sorting children in place would reorder
		// the collection (and its serialized output) as a side effect of a read
		const sortedVersions = [...this.children].sort((a, b) => b.version - a.version);
		return sortedVersions[0];
	}

	/**
	 * @returns a new version of Kitchen cloned from the latest version, null if no kitchen entries exist
	 */
	public newVersionFrom (version: number): Kitchen | null {
		const existingVersion = this.findVersion(version);
		let newVersion: Kitchen | null = null;

		if (existingVersion) {
			newVersion = existingVersion.clone(true);
			this.children.push(newVersion);
			this.context.cache.clear();
		}

		return newVersion;
	}

	/**
	 * @returns a new version of Kitchen cloned from the latest version, null if no kitchen entries exist
	 */
	public newVersionFromLatest (): Kitchen | null {
		const newVersion = this.getLatestVersion()?.clone(true) ?? null;
		// console.log('newVersionFromLatest', newVersion);
		if (newVersion) {
			this.children.push(newVersion);
			this.context.cache.clear();
		}
		return newVersion;
	}

	public promoteDraft (): number | null {
		const draft = this.findVersion(null);
		const latest = this.getLatestVersion();

		if (draft && latest) {
			const draftData = draft.serialize();
			draftData.version = latest.version + 1;
			this.delete(draft.id);
			this.newKitchen(draftData);
			return draftData.version;
		}
		return null;
	}

	public serialize (): KitchensDataOut {
		return {
			children: this.children
				.filter((kitchen: Kitchen) => !kitchen.isTemp)
				.map((kitchen: Kitchen) => kitchen.serialize())
		};
	}
}
